import classNames from 'classnames';
import { type IntlShape, useIntl } from 'react-intl';
import type { Size } from '../../types';
import FlairManager from './images/FlairManager';
import FlairPeak from './images/FlairPeak';
import FlairPlus from './images/FlairPlus';
import styles from './Avatar.module.scss';

export type Flair = 'manager' | 'plus' | 'peak';

const getComponent = (flair: Flair) => {
  switch (flair) {
    case 'manager':
      return FlairManager;
    case 'plus':
      return FlairPlus;
    case 'peak':
      return FlairPeak;
  }
};

const getAriaLabel = (flair: Flair, intl: IntlShape) => {
  switch (flair) {
    case 'manager':
      return intl.formatMessage({ defaultMessage: 'Manager' });
    case 'plus':
      return intl.formatMessage({ defaultMessage: 'Plus' });
    case 'peak':
      return intl.formatMessage({ defaultMessage: 'Peak' });
  }
};

const getSize = (size: Size<'xs' | 'sm' | 'md' | 'lg' | 'xl'>) => {
  switch (size) {
    case 'xs':
      return 12;
    case 'sm':
    case 'md':
      return 16;
    case 'lg':
    case 'xl':
      return 28;
  }
};

type AvatarFlairProps = {
  flair: Flair;
  size: Size<'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'>;
};

const AvatarFlair = ({ flair, size }: AvatarFlairProps) => {
  const intl = useIntl();

  if (size === 'xxs') {
    return null;
  }

  const ariaLabel = getAriaLabel(flair, intl);
  const Component = getComponent(flair);

  // Note that we must use inline SVGs instead of NextJS Image / img because we need to apply styles directly to the SVG element to allow the drop shadow on the SVG element to be visible
  return <Component className={classNames(styles.flair, styles[size])} width={getSize(size)} height={getSize(size)} ariaLabel={ariaLabel} />;
};

export default AvatarFlair;
